import { createAsyncThunk, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';
import { cleanEntity } from 'app/shared/util/entity-utils';
import {
  IQueryParams,
  createEntitySlice,
  EntityState,
  serializeAxiosError,
  IOption,
  BaseResponseEntities,
} from 'app/shared/reducers/reducer.utils';
import { IActivities, defaultValue } from 'app/shared/model/activities/activities-form.model';
import { apiOkr } from 'app/config/axios-interceptor';
import { IActivitiesView } from 'app/shared/model/activities/activities.model';
import qs from 'qs';

const initialState: EntityState<IActivities> = {
  loading: false,
  errorMessage: null,
  entities: [],
  options: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
  dataConfirmCompleteHalfMonth: {},
  dataConfirmCompleteMonth: {},
  dataConfirmCompleteQuarter: {},
  dataConfirmComplete: {},
};

const apiUrl = 'api/okr/activity';
const apiExcelUrl = 'api/okr/excel';

// Actions

interface IQueryParamsActivities extends IQueryParams {
  reportScheduleId?: string[];
  workPriorityLevel?: any[];
  workStatus?: number[];
  alignId?: string[];
  projectId?: string[];
  field?: number[];
  workTypeId?: string[];
  pic?: string[];
  startDate?: string;
  dueDate?: string;
  accountableId: string;
  activityCheckinStatus?: number;
  objectiveId?: string;
}

export const getEntities = createAsyncThunk(
  'activities/fetch_entity_list',
  async ({
    searchText,
    page,
    size,
    sort,
    reportScheduleId,
    workStatus,
    workPriorityLevel,
    accountableId,
    alignId,
    projectId,
    field,
    workTypeId,
    pic,
    startDate,
    dueDate,
    objectiveId,
  }: IQueryParamsActivities) => {
    return apiOkr.get<BaseResponseEntities<IActivitiesView>>(apiUrl, {
      params: {
        searchText,
        pageNum: page,
        pageSize: size,
        sort,
        reportScheduleId,
        workStatus,
        workPriorityLevel,
        accountableId,
        alignId,
        projectId,
        field,
        workTypeId,
        pic,
        startDate: null,
        dueDate: null,
        objectiveId,
      },
      paramsSerializer: params => qs.stringify(params, { arrayFormat: 'repeat' }),
    });
  },
  { serializeError: serializeAxiosError },
);

export const getActivitiesCheckIn = createAsyncThunk(
  'activities/fetch_entity_list_check_in',
  async ({
    searchText,
    page,
    size,
    sort,
    reportScheduleId,
    workStatus,
    workPriorityLevel,
    accountableId,
    alignId,
    projectId,
    field,
    workTypeId,
    pic,
    activityCheckinStatus,
    startDate,
    dueDate,
  }: IQueryParamsActivities) => {
    return apiOkr.get<BaseResponseEntities<IActivitiesView>>(`${apiUrl}/checkin`, {
      params: {
        searchText,
        pageNum: page,
        pageSize: size,
        sort,
        reportScheduleId,
        workStatus,
        workPriorityLevel,
        accountableId,
        alignId,
        projectId,
        field,
        workTypeId,
        pic,
        startDate,
        dueDate,
        activityCheckinStatus,
      },
      paramsSerializer: params => qs.stringify(params, { arrayFormat: 'repeat' }),
    });
  },
  { serializeError: serializeAxiosError },
);

export const exportExcel = createAsyncThunk(
  'activities/exportExcel',
  async (data: any) => {
    const result = await apiOkr.post<any>(`${apiExcelUrl}/export-report-activities`, cleanEntity(data), {
      responseType: 'blob', // Set responseType to 'blob' for binary data
      timeout: 120000,
    });
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const getKrConfirmCompleteMidYear = createAsyncThunk(
  'activities/fetch_list_kr_confirm_complete',
  ({ partyId, planPeriodId }: any, { rejectWithValue }) => {
    const response = apiOkr.get<any>(`/api/okr/checkin-activity/submitted-info-mid-year`, {
      params: {
        partyId,
        planPeriodId,

      },
    }).catch(err => {
      return rejectWithValue(err);
    });
    return response;
  },
);

export const getKrConfirmCompleteHalfMonth = createAsyncThunk(
  'activities/fetch_list_kr_confirm_complete_half_month',
  ({ partyId }: any, { rejectWithValue }) => {
    const response = apiOkr.get<any>(`/api/okr/checkin-activity/submitted-info-half-month/${partyId}`).catch(err => {
      return rejectWithValue(err);
    });
    return response;
  },
);

export const getKrConfirmCompleteQuarter = createAsyncThunk(
  'activities/fetch_list_kr_confirm_complete_quarter',
  ({ partyId }: any, { rejectWithValue }) => {
    const response = apiOkr.get<any>(`/api/okr/checkin-activity/submitted-info-quarter/${partyId}`).catch(err => {
      return rejectWithValue(err);
    });
    return response;
  },
);

export const getKrConfirmCompleteMonth = createAsyncThunk(
  'activities/fetch_list_kr_confirm_complete_month',
  ({ partyId }: any, { rejectWithValue }) => {
    const response = apiOkr.get<any>(`/api/okr/checkin-activity/submitted-info-month/${partyId}`).catch(err => {
      return rejectWithValue(err);
    });
    return response;
  },
);

export const saveCompleteMidYear = createAsyncThunk('checkIn/save_complete_mid_year', async (entity: any, thunkAPI) => {
  const concatEntity = { ...entity, SuccessNote: '.', FailNote: '.', AdjustmentNote: '.', Issue: '.', Solution: '.' };
  const response = await apiOkr.post<any>(`/api/okr/checkin-activity/submitted-mid-year`, concatEntity).catch(err => {
    return thunkAPI.rejectWithValue(err);
  });
  return response;
});

export const saveCompleteQuarter = createAsyncThunk('checkIn/save_complete_quarter', async (entity: any, thunkAPI) => {
  const response = await apiOkr.post<any>(`/api/okr/checkin-activity/submitted-quarter`, entity).catch(err => {
    return thunkAPI.rejectWithValue(err);
  });
  return response;
});

export const saveCompleteMonth = createAsyncThunk('checkIn/save_complete_month', async (entity: any, thunkAPI) => {
  const response = await apiOkr.post<any>(`/api/okr/checkin-activity/submitted-month`, entity).catch(err => {
    return thunkAPI.rejectWithValue(err);
  });
  return response;
});

export const saveCompleteHalfMonth = createAsyncThunk('checkIn/save_complete_half_month', async (entity: any, thunkAPI) => {
  const response = await apiOkr.post<any>(`/api/okr/checkin-activity/submitted-half-month`, entity).catch(err => {
    return thunkAPI.rejectWithValue(err);
  });
  return response;
});

export const getTaskType = createAsyncThunk(
  'activities/fetch_task_type',
  async ({ searchText, page, size, sort, filters }: IQueryParams) => {
    return apiOkr.get<any>(`${apiUrl}/task-type`, {
      params: {
        searchText,
        pageNum: page,
        pageSize: size,
        filters: filters && JSON.stringify(filters),
        sort: sort && sort,
        // cacheBuster: new Date().getTime(),
      },
    });
  },
);

export const getEntity = createAsyncThunk(
  'activities/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return apiOkr.get<IActivities>(requestUrl);
  },
  { serializeError: serializeAxiosError },
);

export const getEntityCheckIn = createAsyncThunk(
  'activities/fetch_entity_checkin',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}/checkin`;
    return apiOkr.get<IActivities>(requestUrl);
  },
  { serializeError: serializeAxiosError },
);

export const checkInEntity = createAsyncThunk(
  'activities/check_in_entity',
  async (entity: IActivities, thunkAPI) => {
    const result = await apiOkr.post<IActivities>(`${apiUrl}/checkin`, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const createEntity = createAsyncThunk(
  'activities/create_entity',
  async (entity: IActivities, thunkAPI) => {
    const result = await apiOkr.post<IActivities>(apiUrl, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const updateEntity = createAsyncThunk(
  'activities/update_entity',
  async (entity: IActivities, thunkAPI) => {
    const result = await apiOkr.put<IActivities>(`${apiUrl}`, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const createEntityAndCreateOther = createAsyncThunk(
  'activities/create_entity_other',
  async (entity: IActivities, thunkAPI) => {
    const result = await apiOkr.post<IActivities>(apiUrl, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const updateEntityAndCreateOther = createAsyncThunk(
  'activities/update_entity_other',
  async (entity: IActivities, thunkAPI) => {
    const result = await apiOkr.put<IActivities>(`${apiUrl}`, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const deleteEntity = createAsyncThunk(
  'activities/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/?activityId=${id}`;
    const result = await apiOkr.delete<IActivities>(requestUrl);
    return result;
  },
  { serializeError: serializeAxiosError },
);

// slice

export const ActivitiesSlice = createEntitySlice({
  name: 'activities',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntityCheckIn.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(exportExcel.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getActivitiesCheckIn), (state, action) => {
        const { data, headers } = action.payload;

        return {
          ...state,
          loading: false,
          entities: data.items,
          totalItems: data.totalRecord,
        };
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        const { data, headers } = action.payload;

        return {
          ...state,
          loading: false,
          entities: data.items,
          totalItems: data.totalRecord,
        };
      })
      .addMatcher(isFulfilled(getTaskType), (state, action) => {
        const { data } = action.payload;

        return {
          ...state,
          options: data,
        };
      })
      .addMatcher(isFulfilled(createEntity, checkInEntity, updateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntities, getEntity, getActivitiesCheckIn, exportExcel, getEntityCheckIn), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, updateEntity, checkInEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      })
      .addMatcher(isFulfilled(getKrConfirmCompleteHalfMonth), (state, action) => {
        state.dataConfirmCompleteHalfMonth = action.payload.data;
        state.loading = false
      })
      .addMatcher(isRejected(getKrConfirmCompleteHalfMonth), (state, action) => {
        state.loading = false
        state.dataConfirmCompleteHalfMonth = {}
      })
      .addMatcher(isFulfilled(getKrConfirmCompleteHalfMonth), (state, action) => {
        state.dataConfirmCompleteHalfMonth = action.payload.data;
        state.loading = false
      })
      .addMatcher(isPending(getKrConfirmCompleteMonth), (state, action) => {
        state.loading = true;
      })
      .addMatcher(isRejected(getKrConfirmCompleteMonth), (state, action) => {
        state.loading = false
        state.dataConfirmCompleteHalfMonth = {}
      })
      .addMatcher(isFulfilled(getKrConfirmCompleteMonth), (state, action) => {
        state.dataConfirmCompleteMonth = action.payload.data;
        state.loading = false
      })
      .addMatcher(isPending(getKrConfirmCompleteQuarter), (state, action) => {
        state.loading = true;
      })
      .addMatcher(isRejected(getKrConfirmCompleteQuarter), (state, action) => {
        state.loading = false
        state.dataConfirmCompleteQuarter = {}
      })
      .addMatcher(isFulfilled(getKrConfirmCompleteQuarter), (state, action) => {
        state.dataConfirmCompleteQuarter = action.payload.data;
        state.loading = false
      })
      .addMatcher(isFulfilled(saveCompleteMidYear), (state, action) => {
        state.loading = false
        state.updateSuccess = true
      })
      .addMatcher(isPending(saveCompleteMidYear), (state, action) => {
        state.loading = true
      })
      .addMatcher(isRejected(saveCompleteMidYear), (state, action) => {
        state.loading = false
        state.updateSuccess = false
      })
      .addMatcher(isFulfilled(saveCompleteQuarter), (state, action) => {
        state.loading = false
        state.updateSuccess = true
      })
      .addMatcher(isPending(saveCompleteQuarter), (state, action) => {
        state.loading = true
      })
      .addMatcher(isRejected(saveCompleteQuarter), (state, action) => {
        state.loading = false
        state.updateSuccess = false
      })
      .addMatcher(isFulfilled(saveCompleteMonth), (state, action) => {
        state.loading = false
        state.updateSuccess = true
      })
      .addMatcher(isFulfilled(saveCompleteHalfMonth), (state, action) => {
        state.loading = false
        state.updateSuccess = true
      })
  },
});

export const { reset } = ActivitiesSlice.actions;

// Reducer
export default ActivitiesSlice.reducer;
